import { useState, useEffect } from 'react';

const HARDCODED_LOCATION = 'Vancouver';
const API_KEY = process.env.REACT_APP_WEATHER_API_KEY;
const API_URL = `https://api.openweathermap.org/data/2.5/weather`;

// Cache configuration
const CACHE_KEY = 'm2m_weather_cache';
const CACHE_DURATION = 60 * 60 * 1000; // 1 hour in milliseconds

// Rate limiting configuration (shared across tabs via localStorage)
const RATE_LIMIT_KEY = 'm2m_weather_rate_limit';
const MIN_FETCH_INTERVAL = 60000; // 1 minute minimum between API calls

function useWeather() {
  const [weatherData, setWeatherData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function fetchWeather() {
      try {
        // LAYER 1: CHECK CACHE FIRST
        const cachedData = getCachedWeather();
        if (cachedData) {
          console.log('✓ Using cached weather data');
          setWeatherData(cachedData);
          setLoading(false);
          return; // Exit early, no API call needed
        }

        // LAYER 2: RATE LIMITING CHECK (cross-tab)
        if (isRateLimited()) {
          console.log('✗ Rate limited - too soon since last fetch (cross-tab protection)');
          setError('Rate limited. Please wait before refreshing.');
          setLoading(false);
          return;
        }

        // Cache miss + rate limit passed - fetch from API
        console.log('→ Cache miss - fetching from API');
        
        // Update rate limit timestamp BEFORE fetch (prevent race conditions)
        updateRateLimit();
        
        const response = await fetch(
          `${API_URL}?q=${HARDCODED_LOCATION}&appid=${API_KEY}&units=metric`
        );
        
        if (!response.ok) {
          throw new Error(`Weather API failed: ${response.status}`);
        }
        
        const data = await response.json();
        
        const condition = mapWeatherCondition(data.weather[0].main);
        
        const processedWeatherData = {
          location: data.name,
          temperature: Math.round(data.main.temp),
          condition: condition,
          rawCondition: data.weather[0].main,
          description: data.weather[0].description
        };
        
        // SAVE TO CACHE
        setCachedWeather(processedWeatherData);
        
        console.log('✓ Weather data fetched and cached');
        setWeatherData(processedWeatherData);
        setLoading(false);
      } catch (err) {
        console.error('✗ Weather API error:', err);
        setError(err.message);
        setLoading(false);
      }
    }

    fetchWeather();
  }, []);

  return { weatherData, loading, error };
}

// ============================================
// CACHE MANAGEMENT (localStorage)
// ============================================

/**
 * Get cached weather data if still valid
 * @returns {Object|null} Weather data or null if cache miss/expired
 */
function getCachedWeather() {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (!cached) return null;

    const { data, timestamp } = JSON.parse(cached);
    const now = Date.now();

    // Check if cache is still valid
    if (now - timestamp < CACHE_DURATION) {
      return data;
    }

    // Cache expired - remove it
    console.log('Cache expired, removing...');
    localStorage.removeItem(CACHE_KEY);
    return null;
  } catch (err) {
    console.error('Cache read error:', err);
    // If cache is corrupted, remove it
    localStorage.removeItem(CACHE_KEY);
    return null;
  }
}

/**
 * Save weather data to cache with timestamp
 * @param {Object} data - Weather data to cache
 */
function setCachedWeather(data) {
  try {
    const cacheObject = {
      data: data,
      timestamp: Date.now()
    };
    localStorage.setItem(CACHE_KEY, JSON.stringify(cacheObject));
  } catch (err) {
    console.error('Cache write error:', err);
    // Non-critical - continue without caching
  }
}

// ============================================
// RATE LIMITING (cross-tab via localStorage)
// ============================================

/**
 * Check if API calls are rate limited (shared across all tabs)
 * @returns {boolean} True if rate limited, false if allowed
 */
function isRateLimited() {
  try {
    const lastFetchStr = localStorage.getItem(RATE_LIMIT_KEY);
    if (!lastFetchStr) return false; // No previous fetch, allow
    
    const lastFetchTime = parseInt(lastFetchStr, 10);
    const now = Date.now();
    
    return (now - lastFetchTime) < MIN_FETCH_INTERVAL;
  } catch (err) {
    console.error('Rate limit check error:', err);
    return false; // On error, allow fetch
  }
}

/**
 * Update rate limit timestamp (shared across all tabs)
 */
function updateRateLimit() {
  try {
    localStorage.setItem(RATE_LIMIT_KEY, Date.now().toString());
  } catch (err) {
    console.error('Rate limit update error:', err);
  }
}

// ============================================
// WEATHER CONDITION MAPPING
// ============================================

/**
 * Map OpenWeatherMap condition to 4 categories
 * @param {string} condition - Raw weather condition from API
 * @returns {string} Mapped condition: 'sunny', 'cloudy', 'rainy', or 'snowy'
 */
function mapWeatherCondition(condition) {
  const conditionMap = {
    'Clear': 'sunny',
    'Clouds': 'cloudy',
    'Rain': 'rainy',
    'Drizzle': 'rainy',
    'Thunderstorm': 'rainy',
    'Snow': 'snowy',
    'Mist': 'cloudy',
    'Smoke': 'cloudy',
    'Haze': 'cloudy',
    'Dust': 'cloudy',
    'Fog': 'cloudy',
    'Sand': 'cloudy',
    'Ash': 'cloudy',
    'Squall': 'cloudy',
    'Tornado': 'cloudy'
  };
  
  return conditionMap[condition] || 'cloudy'; // Default to cloudy
}

// ============================================
// UTILITY: MANUAL CACHE CLEARING (for future use)
// ============================================

/**
 * Clear weather cache and rate limit (useful for manual refresh feature)
 */
export function clearWeatherCache() {
  try {
    localStorage.removeItem(CACHE_KEY);
    localStorage.removeItem(RATE_LIMIT_KEY);
    console.log('Weather cache and rate limit cleared');
  } catch (err) {
    console.error('Cache clear error:', err);
  }
}

export default useWeather;
